import {Component, ErrorInfo, ReactNode} from 'react';

import {logError} from '../../utils/errorLogging';

type SilentErrorBoundaryProps = {
  children: ReactNode;
};

type SilentErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Renders its children normally, but swallows any render throw and renders
 * `null` instead — isolating a GlobalAlert failure from the app-wide
 * `<ErrorBoundary>` (which would otherwise replace the whole app).
 */
export class SilentErrorBoundary extends Component<
  SilentErrorBoundaryProps,
  SilentErrorBoundaryState
> {
  state: SilentErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(): SilentErrorBoundaryState {
    return {hasError: true};
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    // Swallow the error from the UI (render returns null) but still report it to
    // production telemetry (Crashlytics/Sentry via logError) so a dead GlobalAlert
    // mount is observable instead of silently hidden.
    logError(error, 'GlobalAlert.SilentErrorBoundary');
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}
